/**
 * The manager departments (T.Z42, contract v1.8.7) — ONE vocabulary for the whole
 * SPA, framework-free on purpose (the `unit` Playwright project imports it in plain
 * Node; nothing here may touch `import.meta.env` or React).
 *
 * THE OWNER'S WORDING (2026-09-14): the two departments are named IN FULL wherever a
 * person reads them — «Отдел привлечения» and «Отдел сопровождения». The one-letter
 * codes ОП/ОС are the STORAGE spelling (`app_users.department`, the contract's
 * `UserDepartment` enum) and may appear only as a small secondary hint, never as the
 * primary text. So the label map below is the only place the RU words live, and the
 * short form is a suffix the select options do not even use.
 *
 * THE JOIN (why this module exists): the department a surface can filter by. From
 * v1.9.15 (T.Z94) a venue can carry ONE of its own (`#6`'s `department`, the venue
 * card's «Отдел» select) and that field WINS; when it is `null` — the state every
 * venue not yet touched by an admin is in — the venue HAS a department only through
 * its managers, the T.Z42/T.Z56 join below. The api ships the fold's two halves from
 * v1.8.7 on: `#6`'s `managerUserIds` names the managers, and the user directory
 * (#25.5, VIEWER+) names each manager's `department`. Both filters (venues +
 * dashboard) are therefore CLIENT-SIDE joins over two reads the pages already make —
 * no new query parameter, no contract change.
 *
 * THE BUCKETS, kept honest and disjoint:
 *   * `OP` / `OS`       — every department-carrying manager of the venue is in ONE
 *                         department (a venue with managers from only one);
 *   * `MIXED` («Смешанный») — managers from BOTH departments; hiding these inside
 *                         «Без отдела» would let a руководитель miss venues of their
 *                         own department, so the bucket has a name of its own;
 *   * `NONE` («Без отдела») — no manager at all, or managers of whom none has been
 *                         filed under a department yet (the normal state until the
 *                         backfill runs). An unknown/partial payload (an older api
 *                         that shipped no `department`) lands here too — a filter
 *                         must never pretend to know what the api did not say.
 */

/** The storage codes of `app_users.department` (contract `UserDepartment`, v1.8.7). */
export type ManagerDepartment = 'OP' | 'OS';

/** The four buckets a venue (or a user filter) can land in. */
export type DepartmentBucket = ManagerDepartment | 'MIXED' | 'NONE';

/** Full RU names — the ONLY wording a person reads. Order: ОП first, then ОС. */
export const DEPARTMENT_LABEL: Record<ManagerDepartment, string> = {
  OP: 'Отдел привлечения',
  OS: 'Отдел сопровождения',
};

/**
 * The small secondary hint (ОП/ОС) the owner allowed — used in `title` attributes and
 * compact table cells, never as the primary text of a filter option or a column.
 */
export const DEPARTMENT_SHORT: Record<ManagerDepartment, string> = {
  OP: 'ОП',
  OS: 'ОС',
};

/** The bucket labels, including the two buckets that are not a department. */
export const DEPARTMENT_BUCKET_LABEL: Record<DepartmentBucket, string> = {
  OP: DEPARTMENT_LABEL.OP,
  OS: DEPARTMENT_LABEL.OS,
  MIXED: 'Смешанный',
  NONE: 'Без отдела',
};

/** The buckets the venues/dashboard filter offers, in the order the options print. */
export const DEPARTMENT_BUCKETS: ReadonlyArray<DepartmentBucket> = ['OP', 'OS', 'NONE', 'MIXED'];

/** The buckets a USER row can be filtered by — a person cannot be two departments. */
export const USER_DEPARTMENT_BUCKETS: ReadonlyArray<ManagerDepartment | 'NONE'> = ['OP', 'OS', 'NONE'];

/** The wire value of #25/#25.1/#25.2 — a code, `null`, or an absent key. */
export type DepartmentWire = ManagerDepartment | null | undefined;

/** Narrows the wire value to the two codes; anything else (an older api, a stub) is "none". */
export function asDepartment(value: DepartmentWire): ManagerDepartment | null {
  return value === 'OP' || value === 'OS' ? value : null;
}

/** A user row's department, as the filters and the drawer read it (`null` = «без отдела»). */
export function departmentOf(row: { department?: DepartmentWire }): ManagerDepartment | null {
  return asDepartment(row.department);
}

/** The full RU name of a user row's department, «Без отдела» included for the cells. */
export function departmentLabelOf(row: { department?: DepartmentWire }): string {
  const department = asDepartment(row.department);
  return department === null ? 'Без отдела' : DEPARTMENT_LABEL[department];
}

// ---------------------------------------------------------------------------
// The venue → department join
// ---------------------------------------------------------------------------

/** What the join reads off a venue: the managers #6 already names. */
export interface VenueManagersLike {
  managerUserIds?: ReadonlyArray<number> | null;
  /**
   * ADDITIVE (v1.9.15, T.Z94) — the venue's OWN department (`venues.department`,
   * migration 0026, the venue card's «Отдел» select): `OP`/`OS`, `null` = «не задано».
   * Optional in the type on purpose — an older api (or a hand-built stub) sends
   * neither, and such a row degrades to the managers' fold without a runtime hole.
   * THE TWO-TIER RULE lives in {@link departmentBucketOfVenue}: this field, when it is
   * a real department, WINS over the fold; when it is `null` the fold decides exactly
   * as before, so a venue the admin has not touched never moves.
   */
  department?: DepartmentWire;
}

/**
 * What the join reads off a DASHBOARD row (#11): the same manager ids under a
 * different name. #11 does not carry them, so the page joins through the #6
 * directory (`venueId → managerUserIds`) — this alias keeps one join for both.
 */
export type DashboardRowLike = VenueManagersLike & { venueId: number };

/** What the join reads off a directory entry: the department v1.8.7 ships. */
export interface DepartmentSource {
  department?: DepartmentWire;
}

/**
 * A venue's department bucket — the TWO-TIER read (T.Z94, v1.9.15):
 *
 *   * TIER 1 — the venue's OWN department (`venue.department`, the venue card's
 *     «Отдел» select). When the row carries a real department it WINS: an explicit
 *     assignment on the venue outranks whatever the managers imply, in EVERY
 *     consumer of this fold (the roster's client-side «Отдел» filter, the option
 *     counts, the «Распределение ЦА» and «Квоты» row folds, the dashboard's
 *     region-scoped counts).
 *   * TIER 2 — the managers' fold (T.Z42/T.Z56), unchanged for a venue the admin has
 *     not touched: `venue.department === null` (or the key absent — an older api, a
 *     stub) falls through to the counter-based fold below, so a venue nobody has
 *     assigned answers exactly what it answered before T.Z94.
 *
 * The fold itself is deliberately COUNTER-BASED, not first-hit-based: a venue managed
 * by one ОП and one ОС manager is «Смешанный», and a venue whose only manager is
 * unfiled is «Без отдела» — both facts the owner asked to be able to see. `undefined`
 * department values (an api older than v1.8.7, or a payload that omits the key) count
 * as "unknown", which is the same as "none" for the bucket and for the counts.
 *
 * `MIXED` stays DERIVED-only: the api refuses to store anything but `OP`/`OS` on the
 * venue (the CHECK holds it), so a hand assignment is the way a руководитель resolves
 * a «Смешанный» venue — until then it keeps answering «Смешанный».
 */
export function departmentBucketOfVenue(
  venue: VenueManagersLike,
  usersById: ReadonlyMap<number, DepartmentSource> | undefined,
): DepartmentBucket {
  const own = asDepartment(venue.department);
  if (own !== null) return own;
  let op = 0;
  let os = 0;
  for (const id of venue.managerUserIds ?? []) {
    if (!Number.isInteger(id) || id < 1) continue;
    const department = usersById === undefined ? undefined : asDepartment(usersById.get(id)?.department);
    if (department === 'OP') op += 1;
    else if (department === 'OS') os += 1;
  }
  if (op > 0 && os > 0) return 'MIXED';
  if (op > 0) return 'OP';
  if (os > 0) return 'OS';
  return 'NONE';
}

/** True when a venue's bucket is the one the filter asks for (`undefined` = no filter). */
export function venueMatchesDepartment(
  venue: VenueManagersLike,
  usersById: ReadonlyMap<number, DepartmentSource> | undefined,
  bucket: DepartmentBucket | undefined,
): boolean {
  if (bucket === undefined) return true;
  return departmentBucketOfVenue(venue, usersById) === bucket;
}

/**
 * The bucket of EVERY directory venue at once — the pre-computed index a page that
 * holds rows WITHOUT the manager ids (the dashboard: #11 answers `venueId` only)
 * filters through. A venue the directory does not know lands in `NONE` there: the
 * join must never pretend a row is "known" when the api said nothing about it.
 */
export function departmentBucketsByVenue(
  venues: ReadonlyArray<VenueManagersLike & { id: number }>,
  usersById: ReadonlyMap<number, DepartmentSource> | undefined,
): ReadonlyMap<number, DepartmentBucket> {
  const buckets = new Map<number, DepartmentBucket>();
  for (const venue of venues) {
    buckets.set(venue.id, departmentBucketOfVenue(venue, usersById));
  }
  return buckets;
}

/** True when a row's pre-computed bucket is the one the filter asks for. */
export function rowInDepartmentBucket(
  row: { venueId: number },
  buckets: ReadonlyMap<number, DepartmentBucket>,
  bucket: DepartmentBucket | undefined,
): boolean {
  if (bucket === undefined) return true;
  return (buckets.get(row.venueId) ?? 'NONE') === bucket;
}

/**
 * The rows that survive the filter, as ONE fold over a pre-computed bucket index
 * (ADDITIVE, T.Z56) — the client-side half of the roll-out: the «Распределение ЦА» and
 * «Квоты» grids filter here because #11.5/#12.5 answer the whole venue-joined roster in
 * one unpaginated read, while the paged/aggregate surfaces (#3/#7.5/#11) narrow the same
 * buckets SERVER-SIDE. A row the index does not know reads as `NONE` — the join never
 * invents knowledge. `undefined` bucket = no filter = every row, as a fresh array.
 */
export function rowsInDepartmentBucket<Row extends { venueId: number }>(
  rows: ReadonlyArray<Row>,
  buckets: ReadonlyMap<number, DepartmentBucket>,
  bucket: DepartmentBucket | undefined,
): Row[] {
  if (bucket === undefined) return [...rows];
  return rows.filter((row) => rowInDepartmentBucket(row, buckets, bucket));
}

/** The counts behind the filter options, over ROWS and a pre-computed bucket index. */
export function countRowsByDepartment(
  rows: ReadonlyArray<{ venueId: number }>,
  buckets: ReadonlyMap<number, DepartmentBucket>,
): DepartmentCounts {
  const counts = { OP: 0, OS: 0, NONE: 0, MIXED: 0 };
  for (const row of rows) {
    counts[buckets.get(row.venueId) ?? 'NONE'] += 1;
  }
  return counts;
}

/** The counts behind the filter options — one pass, over the whole directory. */
export interface DepartmentCounts {
  readonly OP: number;
  readonly OS: number;
  readonly NONE: number;
  readonly MIXED: number;
}

export function countVenueDepartments(
  venues: ReadonlyArray<VenueManagersLike>,
  usersById: ReadonlyMap<number, DepartmentSource> | undefined,
): DepartmentCounts {
  const counts = { OP: 0, OS: 0, NONE: 0, MIXED: 0 };
  for (const venue of venues) {
    counts[departmentBucketOfVenue(venue, usersById)] += 1;
  }
  return counts;
}

/**
 * The «Отдел» select options with their counts: «Все (189)» / «Отдел привлечения
 * (12)» / «Отдел сопровождения (8)» / «Без отдела (161)» / «Смешанный (8)». `null`
 * counts (a failed or absent directory) keep the bare labels, exactly like the
 * «Тип» filter's options do.
 */
export function departmentFilterOptions(
  counts: DepartmentCounts | null | undefined,
): Array<{ value: string; label: string; title: string }> {
  const all = counts ? `Все (${counts.OP + counts.OS + counts.NONE + counts.MIXED})` : 'Все';
  const withCount = (bucket: DepartmentBucket): string => {
    const label = DEPARTMENT_BUCKET_LABEL[bucket];
    return counts ? `${label} (${counts[bucket]})` : label;
  };
  return [
    { value: '', label: all, title: 'Площадки всех отделов и без отдела' },
    { value: 'OP', label: withCount('OP'), title: 'Площадки менеджеров Отдела привлечения (ОП)' },
    { value: 'OS', label: withCount('OS'), title: 'Площадки менеджеров Отдела сопровождения (ОС)' },
    { value: 'NONE', label: withCount('NONE'), title: 'У площадки нет менеджера с назначенным отделом' },
    { value: 'MIXED', label: withCount('MIXED'), title: 'Менеджеры площадки из обоих отделов' },
  ];
}

/** The users-tab variant: «Все отделы» / the two departments / «Без отдела». */
export function userDepartmentFilterOptions(): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'Все отделы' },
    { value: 'OP', label: DEPARTMENT_LABEL.OP },
    { value: 'OS', label: DEPARTMENT_LABEL.OS },
    { value: 'NONE', label: 'Без отдела' },
  ];
}

/** The select options of the «Отдел» editor: «—» = no department. */
export function departmentSelectOptions(): Array<{ value: string; label: string }> {
  return [
    { value: '', label: '— без отдела' },
    { value: 'OP', label: DEPARTMENT_LABEL.OP },
    { value: 'OS', label: DEPARTMENT_LABEL.OS },
  ];
}

/**
 * The select options of the VENUE card's «Отдел» editor (T.Z94, v1.9.15) — the same
 * two departments as the users drawer's, but the empty option says «не задано», not
 * «без отдела»: a venue with an unset field is not a venue WITHOUT a department (its
 * department is derived from the managers until the field is set), and the label must
 * not claim a state the data does not hold.
 */
export function venueDepartmentSelectOptions(): Array<{ value: string; label: string }> {
  return [
    { value: '', label: '— не задано' },
    { value: 'OP', label: DEPARTMENT_LABEL.OP },
    { value: 'OS', label: DEPARTMENT_LABEL.OS },
  ];
}

/**
 * The suffix a counter line prints while a department filter narrows a list —
 * «Показано 7 из 19 · Отдел привлечения» — so the active filter is readable without
 * opening the select. Empty string when the filter is off.
 */
export function departmentFilterMetaLabel(bucket: DepartmentBucket | null | undefined): string {
  return bucket === null || bucket === undefined ? '' : ` · ${DEPARTMENT_BUCKET_LABEL[bucket]}`;
}

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

/**
 * `OP | OS | NONE | MIXED` or nothing — a hand-edited `?department=ОП` falls back to
 * «Все», never to a request the api would have to refuse (the filter is client-side
 * anyway, but the URL must not carry a value the options cannot render).
 */
export function parseDepartmentBucket(value: string | undefined): DepartmentBucket | undefined {
  return value === 'OP' || value === 'OS' || value === 'NONE' || value === 'MIXED' ? value : undefined;
}
